'use strict';
/* ==========================================================================
   levels/title.js — the title (DESIGN.md §2): Max's science-fair table in a school gym, on Energy's BLUEPRINT paper.
   The blueprint wall (core-local/fair.js Fair.backdrop), the gym floor, a table with Max's tri-fold poster: a blue law
   card stamped WHAT beside an amber theory card stamped WHY (no ladder between them). Ransom-letter title, the subtitle
   in hand lettering, Max (left, arms crossed) and Dr. Voss (right) rising in, ▶ Play → the map. On a phone held
   upright the controls move to the portrait dock (Dock.title) and the stage keeps only the art.
   Levels.title.loading() draws the "checking the evidence…" screen while assets load (no textures yet: plain fills
   and ink only).
   Conventions (shared/engine/v2/README.md): draw through g, a pure function of time (T, this.enteredAt), tappable
   sizes through UI.button / UI.tap, no per-frame shadowOn() (fair.js pieces are cached paper).
   ========================================================================== */
(function () {
  const PLAY = { x: 960, y: 985 };
  const F = () => window.Fair;

  /* the title on 1–2 ransom lines, split at the most even word break */
  function titleLines(text) {
    const words = String(text).toUpperCase().split(/\s+/).filter(Boolean);
    if (words.join(' ').length <= 12) return [words.join(' ')];
    let best = null;
    for (let i = 1; i < words.length; i++) {
      const a = words.slice(0, i).join(' '), b = words.slice(i).join(' '), sc = Math.max(a.length, b.length);
      if (!best || sc < best.sc) best = { sc, L: [a, b] };
    }
    return best.L;
  }

  function stage(t0) {
    const Fa = F(), C = Fa.C;
    Fa.backdrop();
    Fa.floor(900);
    // Max's table + the tri-fold poster with the two cards side by side
    const ts = pop(T, t0 + 0.1, 0.45, 1.6);
    Fa.table(960, 872, 1000, { seed: 21, s: ts });
    const bs = pop(T, t0 + 0.3, 0.45, 1.8);
    if (bs > 0) {
      Fa.board(960, 650, 820, 420, { title: 'DIFFERENT JOBS', seed: 51, s: bs, art: true });
      Fa.card('law', 960 - 150, 690, 250, 170, { title: 'LAW', body: 'what happens', seed: 71, rot: -0.03, s: bs, t0: t0 + 0.8 });
      Fa.card('theory', 960 + 150, 694, 250, 170, { title: 'THEORY', body: 'why it happens', seed: 73, rot: 0.025, s: bs, t0: t0 + 1.0 });
    }
    tape(120, 40, 170, -0.5, undefined, 2); tape(1800, 40, 170, 0.5, undefined, 3);
    return C;
  }

  class Title extends Scene {
    enter() { this.started = false; }
    go() {
      if (this.started) return;
      this.started = true;
      Audio.unlock();
      if (Game.MUSIC) Audio.music(Game.MUSIC, { gain: 0.3 });
      Game.replace(new Levels.map());
    }
    keyDown(k) { if (k === 'Enter' || k === ' ') this.go(); }

    draw() {
      UI.allLessons();                       // "← All lessons" (a paper tab; in the portrait dock a button in its bar)
      const t = this.t, t0 = this.enteredAt, L = Game.lesson || {};
      const C = stage(t0);

      // the title: ransom letters, then the subtitle in cream hand lettering
      const lines = titleLines(L.title || "It's Only a Theory?!");
      const size = lines.length > 1 ? 112 : 130;
      lines.forEach((l, i) => ransom(l, 960, 120 + i * size * 1.12, size, t0 + 0.3 + i * 0.35, { seed: 101 + i * 13 }));
      const ySub = 120 + (lines.length - 1) * size * 1.12 + size * 0.95;
      hand(L.subtitle || '', 960, ySub, 64, t0 + 1.2, { dur: 0.5, color: C.cream, rot: -0.02, outline: 'rgba(37,60,90,0.9)' });

      // the cast rises in from below: Max (left, arms crossed), Dr. Voss (right)
      const rise = n => E.outBack(P(T, t0 + n, t0 + n + 0.5), 1.4);
      Puppet.draw('max', 230, 1080 + (1 - rise(0.6)) * 520, 580, { facing: 1, t: T, pose: 'cross' });
      Puppet.draw('voss', 1690, 1080 + (1 - rise(0.8)) * 520, 640, { facing: -1, t: T });

      // portrait phone: the controls are in the dock (core/dock.js Dock.title); the stage keeps only the art
      if (UI.docked()) { Dock.title({ onStart: () => this.go(), onContinue: () => this.go() }); return; }
      if (t > 1.4 && UI.button('play', '▶ Play', PLAY.x, PLAY.y, { w: 380, h: 116, color: C.red, font: "'Luckiest Guy'", size: 64, rot: -0.015 })) this.go();
      if (t > 2.4) hand(UI.hint('(or press Enter)', 'tap ▶ Play to start'), PLAY.x + 330 + (UI.tap(116) - 116) / 2, PLAY.y + 8, UI.text(30), t0 + 2.4, { color: C.cream, seed: 61, align: 'left', outline: 'rgba(37,60,90,0.8)' });
    }
  }
  Title.PLAY = PLAY;                         // tools/integration.js clicks it
  Title.titleLines = titleLines;

  /* loading screen: no textures yet → plain blueprint blue, a pencil ticking boxes on an index card */
  Title.loading = function () {
    g.fillStyle = '#2f5f8f'; g.fillRect(0, 0, Game.W, Game.H);
    g.fillStyle = '#fbf5e6'; g.fillRect(660, 380, 600, 260);
    const n = 4, k = Math.floor((T * 3) % (n + 2));
    for (let i = 0; i < n; i++) {
      const y = 430 + i * 52;
      g.lineWidth = 3; g.strokeStyle = '#2b2420'; g.strokeRect(700, y - 16, 32, 32);
      ink([[750, y], [1210, y]], { w: 2, color: 'rgba(90,150,190,0.6)', seed: 40 + i, passes: 1, step: 60 });
      if (i < k) ink([[704, y], [714, y + 12], [736, y - 20]], { w: 6, color: '#c8321f', seed: 50 + i, passes: 1, step: 8 });
    }
    hand('checking the evidence…', 960, 720, 64, 0, { dur: 0.01, seed: 5, color: '#fbf5e6' });
  };

  Levels.title = Title;
})();
